import { z } from 'zod';
import { mapValues } from 'remeda';
import type { NBAMarketCode } from '@sharpr/types';
import { GameIdSchema, IsoDateTimeSchema, MarketCodeSchema, PlayerIdSchema, SlateDatePatternSchema, TeamIdSchema } from '../ids';
import { pmfBins } from '../pmf';
import { parseScenarioKey, scenarioKey } from '../scenario';

// The NBA members of the projection core's per-sport documents: stored (camelCase) and Python wire (snake_case).

export const NBA_PMF_MAX_BINS = 512;
export const NBA_TEAM_MINUTES = 240;
export const NBA_MAX_PLAYER_MINUTES = 48;
// A team's questionable players at most: every combination is projected, so a game has up to 2^3 × 2^3 = 64 scenarios.
export const NBA_MAX_QUESTIONABLE = 3;
// A rotation's starters (the analyst's marker) at most.
export const NBA_STARTERS = 5;

// The fields both casings share, each bound defined once.
const NbaMinutesSchema = z.number().min(0).max(NBA_MAX_PLAYER_MINUTES);
export const NbaMinutesByPlayerSchema = z.record(PlayerIdSchema, NbaMinutesSchema);
const rotationOuts = z.array(PlayerIdSchema).max(15);   // one team's out players
const scenarioOuts = z.array(PlayerIdSchema).max(30);   // a game scenario's: everyone out across both teams (§3.2)
const NBA_TARGET_STATS = {
  pts: z.number().positive().max(200).nullable(),
  reb: z.number().positive().max(100).nullable(),
  ast: z.number().positive().max(100).nullable(),
};

// ── Stored documents (§13.1) ──

// A rotation's minutes total the team's 240 (minutes are tenths; the tolerance absorbs float sums).
export const nbaTotalsTeamMinutes = (minutes: Record<string, number>): boolean =>
  Math.abs(Object.values(minutes).reduce((a, b) => a + b, 0) - NBA_TEAM_MINUTES) <= 1e-6;

const NbaRotationSchema = z.strictObject({ outPlayerIds: rotationOuts, minutes: NbaMinutesByPlayerSchema })
  .refine(r => new Set(r.outPlayerIds).size === r.outPlayerIds.length, 'outPlayerIds must be unique')
  .refine(r => r.outPlayerIds.every(id => !(id in r.minutes)), 'an out player receives no minutes')
  .refine(r => nbaTotalsTeamMinutes(r.minutes), { message: `team minutes must total ${NBA_TEAM_MINUTES}`, path: ['minutes'] });

// A home × away pairing's team targets, keyed by the game scenario's out players and the team. A missing entry is Auto
// for all three; null is Auto for that stat. Entries for pairings that no longer exist are ignored by the model.
const NbaTargetSchema = z.strictObject({ outPlayerIds: scenarioOuts, teamId: TeamIdSchema, ...NBA_TARGET_STATS });
const nbaTargetIdentity = (t: { outPlayerIds: string[]; teamId: string }) => `${[...t.outPlayerIds].sort().join(',')}|${t.teamId}`;

export const NbaInputsSchema = z.strictObject({
  rotations: z.record(TeamIdSchema, z.array(NbaRotationSchema).min(1)
    .refine(rs => new Set(rs.map(r => scenarioKey(r.outPlayerIds))).size === rs.length, 'one rotation per set of out players')
    .refine(rs => rs.some(r => r.outPlayerIds.length === 0), 'a team needs its Base rotation (nobody out)')
    // A scenario's out players are ones the Base rotation plays (more than 0 minutes: the runner leaves a 0-minute player
    // out, so no scenario would project him): they become the game's questionable players, and the seed keys its
    // scenarios by them (§13.3).
    .refine(rs => { const base = rs.find(r => r.outPlayerIds.length === 0); return !base || rs.every(r => r.outPlayerIds.every(id => (base.minutes[id] ?? 0) > 0)); },
      'an out player must be one the Base rotation plays')
    // A team's rotations cover every combination of its questionable players (each set of them out once: with one
    // rotation per set, 2^n rotations), so any viewer's marks and any analyst ruling were projected.
    .refine(rs => new Set(rs.flatMap(r => r.outPlayerIds)).size <= NBA_MAX_QUESTIONABLE, `a team has at most ${NBA_MAX_QUESTIONABLE} questionable players`)
    .refine(rs => rs.length === 2 ** new Set(rs.flatMap(r => r.outPlayerIds)).size, 'a team\'s rotations cover every combination of its questionable players')),
  targets: z.array(NbaTargetSchema).optional()
    .refine(ts => !ts || new Set(ts.map(nbaTargetIdentity)).size === ts.length, 'one target per game scenario and team'),
}).superRefine((inputs, ctx) => {
  // A player belongs to one team's rotations, so each game scenario is exactly one home × away pairing. The player rides
  // in params, for a caller to name.
  const teamOf = new Map<string, string>();
  for (const [teamId, rotations] of Object.entries(inputs.rotations)) {
    for (const id of new Set(rotations.flatMap(r => [...r.outPlayerIds, ...Object.keys(r.minutes)]))) {
      if (teamOf.has(id) && teamOf.get(id) !== teamId) ctx.addIssue({ code: 'custom', path: ['rotations'], message: 'in both teams\' rotations', params: { playerId: id } });
      teamOf.set(id, teamId);
    }
  }
});
export type NbaInputs = z.infer<typeof NbaInputsSchema>;

// The game's scenarios: every home rotation paired with every away rotation (home first), each as its out players in
// key order. These are exactly the scenarios a run of the inputs projects.
export function nbaGameScenarios(inputs: { rotations: Record<string, ReadonlyArray<{ outPlayerIds: readonly string[] }>> }, homeId: string, awayId: string): string[][] {
  const away = inputs.rotations[awayId] ?? [];
  return (inputs.rotations[homeId] ?? []).flatMap(h => away.map(a => parseScenarioKey(scenarioKey([...h.outPlayerIds, ...a.outPlayerIds])).outPlayerIds));
}

// A player's minutes in one game scenario: his team's rotation's.
export const NbaWorkloadSchema = z.strictObject({ minutes: NbaMinutesSchema });

// The DFS sites' classic NBA scoring, per counted market (a made three scores its points and DraftKings' 0.5 on top), and
// their bonuses: DraftKings pays +1.5 for a double-double (10+ in two of NBA_DOUBLE_STATS) and +3 for a triple-double, on
// top of it: DraftKings' rules (api.draftkings.com/rules-and-scoring/RulesAndScoring.json) list them as separate bonus
// categories, each "Max 1 Per Player" ("Per Game": "Bonus categories are awarded on a per game basis"). A player's
// expected points are priced from his effective PMFs (fantasy.ts: nbaFantasyPoints).
export const NBA_DFS_SITES = ['draftkings', 'fanduel'] as const;
export type NbaDfsSite = (typeof NBA_DFS_SITES)[number];
export const NBA_DFS_SCORING: Record<NbaDfsSite, Partial<Record<NBAMarketCode, number>>> = {
  draftkings: { pts: 1, '3pm': 0.5, reb: 1.25, ast: 1.5, stl: 2, blk: 2, to: -0.5 },
  fanduel: { pts: 1, reb: 1.2, ast: 1.5, stl: 3, blk: 3, to: -1 },
};
export const NBA_DFS_BONUS: Record<NbaDfsSite, { doubleDouble: number; tripleDouble: number }> = {
  draftkings: { doubleDouble: 1.5, tripleDouble: 3 },
  fanduel: { doubleDouble: 0, tripleDouble: 0 },
};
export const NBA_DOUBLE_STATS = ['pts', 'reb', 'ast', 'stl', 'blk'] as const;
export const NBA_DOUBLE = 10;
type NbaTeamStats = Record<keyof typeof NBA_TARGET_STATS, number>;
// The model's own points, rebounds and assists (before the line and any target) and what the team was priced to:
// points held to the analyst's target else the line (blended with the model's own when the fit carries weights),
// rebounds and assists at the analyst's targets else as the line moved them.
export type NbaTeamTotals = NbaTeamStats & { priced: NbaTeamStats };

// ── Python wire (§13.2, §13.9) ──
// The wire is snake_case and structural; refines and mass rules are re-asserted on the mapped documents by the callback.

// The game line the model prices at: one book's main spread (home) and total, the app's choice. Python copies it into
// the artifact as given.
const NbaGameLineSchema = z.strictObject({
  book_id: z.string().min(1), home_spread: z.number(), total: z.number().positive(),
  spread_updated_at: IsoDateTimeSchema, total_updated_at: IsoDateTimeSchema,
});
export type NbaGameLine = z.infer<typeof NbaGameLineSchema>;

const NbaRunnerRotationSchema = z.strictObject({ out_player_ids: rotationOuts, minutes: NbaMinutesByPlayerSchema });
type NbaRunnerRotation = z.infer<typeof NbaRunnerRotationSchema>;
// A game scenario travels as its list of out players (both teams'), never as a key string: the model matches its
// home × away pairings by out set and needs no key format (§3.2).
const NbaRunnerTargetSchema = z.strictObject({ out_player_ids: scenarioOuts, team_id: TeamIdSchema, ...NBA_TARGET_STATS });
// A game's runner inputs: the analyst's rotations and targets, and its game line. The artifact echoes them.
export const NbaRunnerInputsSchema = z.strictObject({
  rotations: z.record(TeamIdSchema, z.array(NbaRunnerRotationSchema).min(1)),
  targets: z.array(NbaRunnerTargetSchema),
  game_line: NbaGameLineSchema,
});
export type NbaRunnerInputs = z.infer<typeof NbaRunnerInputsSchema>;

export function nbaInputsForRunner(inputs: NbaInputs, gameLine: NbaGameLine): NbaRunnerInputs {
  return {
    rotations: mapValues(inputs.rotations, rotations => rotations.map(r => ({ out_player_ids: r.outPlayerIds, minutes: r.minutes }))),
    targets: (inputs.targets ?? []).map(t => ({ out_player_ids: t.outPlayerIds, team_id: t.teamId, pts: t.pts, reb: t.reb, ast: t.ast })),
    game_line: gameLine,
  };
}

// A priced game scenario, once: its out players, and its two teams [home, away] with the model's own points, rebounds and
// assists (before the line and any target, the bench row's included) and what each was priced to; each
// with its listed players' PMFs (the NBA bin cap, emitted into the JSON Schema). Loose: a Python-added field never fails
// the run.
const nbaTeamStat = z.number().nonnegative();
const NbaArtifactPlayerSchema = z.looseObject({
  player_id: PlayerIdSchema, market_pmfs: z.partialRecord(MarketCodeSchema, z.looseObject({ pmf: pmfBins(NBA_PMF_MAX_BINS) })),
});
const NbaArtifactTeamSchema = z.looseObject({
  team_id: TeamIdSchema, model_points: nbaTeamStat, model_rebounds: nbaTeamStat, model_assists: nbaTeamStat,
  priced_points: nbaTeamStat, priced_rebounds: nbaTeamStat, priced_assists: nbaTeamStat,
  players: z.array(NbaArtifactPlayerSchema).min(1),
});
export const NbaArtifactScenarioSchema = z.looseObject({ out_player_ids: scenarioOuts, teams: z.array(NbaArtifactTeamSchema).length(2) });
export type NbaArtifactScenario = z.infer<typeof NbaArtifactScenarioSchema>;

// The two rotations a scenario was priced at: the one home × away pair whose out players are, together, exactly the
// scenario's (home and away rotations name disjoint players, so a pair splits the outs by team).
function nbaScenarioRotations(inputs: NbaRunnerInputs, s: NbaArtifactScenario): [NbaRunnerRotation, NbaRunnerRotation] {
  const key = scenarioKey(s.out_player_ids);
  const [home, away] = s.teams;
  const pairs = (inputs.rotations[home.team_id] ?? []).flatMap(h => (inputs.rotations[away.team_id] ?? [])
    .filter(a => scenarioKey([...h.out_player_ids, ...a.out_player_ids]) === key)
    .map((a): [NbaRunnerRotation, NbaRunnerRotation] => [h, a]));
  if (pairs.length !== 1) throw new Error(`${pairs.length} rotation pairs price scenario ${key}`);
  return pairs[0];
}

// An NBA artifact's player and team projections (snake → camel): a player's workload is his minutes in the rotation his
// scenario was priced at, a team's totals the model's points, rebounds and assists. Unknown keys Python adds are dropped.
// A run that projects a game projects every scenario its inputs make, once each: a ruling is live once any stored
// scenario sits the player, so a missing pairing would leave the ruled scenario without rows.
export function nbaProjectionsFromArtifact(artifact: { inputs: NbaRunnerInputs; scenarios: readonly NbaArtifactScenario[] }) {
  const priced = artifact.scenarios.map(s => ({ s, scenario: scenarioKey(s.out_player_ids), rotations: nbaScenarioRotations(artifact.inputs, s) }));
  const sent = new Set<string>();
  for (const { scenario } of priced) {
    if (sent.has(scenario)) throw new Error(`the run sent scenario ${scenario} twice`);
    sent.add(scenario);
  }
  const [first] = artifact.scenarios;
  if (first) {
    const rotations = mapValues(artifact.inputs.rotations, rs => rs.map(r => ({ outPlayerIds: r.out_player_ids })));
    const missing = nbaGameScenarios({ rotations }, first.teams[0].team_id, first.teams[1].team_id).map(scenarioKey).find(k => !sent.has(k));
    if (missing !== undefined) throw new Error(`the run left out scenario ${missing}`);
  }
  const players = priced.flatMap(({ s, scenario, rotations }) => s.teams.flatMap((t, i) => t.players.map(p => {
    const minutes = rotations[i].minutes[p.player_id];
    if (minutes === undefined) throw new Error(`${t.team_id}'s rotation for ${scenario} gives player ${p.player_id} no minutes`);
    return { playerId: p.player_id, scenario, teamId: t.team_id, workload: { minutes }, marketPmfs: mapValues(p.market_pmfs, m => ({ pmf: m.pmf })) };
  })));
  // A player's team is read from his own rows: an out player the run projects in no scenario has none.
  const projected = new Set(players.map(p => p.playerId));
  const unprojected = priced.flatMap(({ s }) => s.out_player_ids).find(id => !projected.has(id));
  if (unprojected !== undefined) throw new Error(`out player ${unprojected} has no projection in any scenario`);
  return {
    players,
    teams: priced.flatMap(({ s, scenario }) => s.teams.map(t => ({
      scenario, teamId: t.team_id, totals: {
        pts: t.model_points, reb: t.model_rebounds, ast: t.model_assists,
        priced: { pts: t.priced_points, reb: t.priced_rebounds, ast: t.priced_assists },
      },
    }))),
  };
}

// The official game logs the nightly sync posts (POST /api/model/game-logs/nba): finished games, each whole — every player
// who played, the stats ROI grades (null: the log lacks it). A posted game replaces what was stored for it. Snake_case and
// structural, like the runner's wire (the route checks the rest).
const nbaStat = z.number().nonnegative().nullable();
const NbaGameLogRowSchema = z.strictObject({
  player_id: PlayerIdSchema, player_name: z.string().min(1), opp: z.string().min(1),
  min: nbaStat, pts: nbaStat, reb: nbaStat, ast: nbaStat, stl: nbaStat, blk: nbaStat, tov: nbaStat, fg3m: nbaStat,
});
const NbaGameLogSchema = z.strictObject({ game_id: GameIdSchema.length(10), game_date: SlateDatePatternSchema, rows: z.array(NbaGameLogRowSchema).min(1).max(40) });
export const NbaGameLogsSchema = z.strictObject({ games: z.array(NbaGameLogSchema).min(1).max(250) });

// The official NBA schedule the nightly sync posts (POST /api/model/schedule/nba): every game whose two teams are known,
// scheduled or postponed. apply_schedule writes games posted with a future tip-off (a game postponed on game day is
// re-dated); the rest are skipped there.
const NbaScheduledGameSchema = z.strictObject({
  game_id: GameIdSchema.length(10), commence_time: z.iso.datetime({ offset: true }), home_team_id: TeamIdSchema, away_team_id: TeamIdSchema,
  status: z.enum(['scheduled', 'postponed']),
});
export const NbaScheduleSchema = z.strictObject({ games: z.array(NbaScheduledGameSchema).min(1).max(2000) });

// The official NBA rosters the nightly sync posts (POST /api/model/rosters/nba): every player on a roster (ROSTER_STATUS 1)
// in cdn.nba.com's player index. Snake_case, like the schedule and the game logs; the route converts. The route checks the rest (every team present, a minimum count).
const NbaRosterPlayerSchema = z.strictObject({ player_id: PlayerIdSchema, name: z.string().min(1), team_abbr: z.string().min(1), position: z.string() });
export const NbaRostersSchema = z.strictObject({ players: z.array(NbaRosterPlayerSchema).min(1).max(1500) });
